/**
 * FixErrorsPage is the orchestrator for the fix-error-and-import flow.
 * Used by both property bulk import and order bulk import routes.
 *
 * Reads search params for fileId, success/failed counts, and isProperty flag.
 * Delegates to:
 *  - useFixErrors    (state + API calls)
 *  - FixErrorsHeader (back button, visible for property flow)
 *  - FixErrorsModals (4 modal variants)
 *  - FixErrorsActions (footer buttons)
 *  - ErrorDetailsTable (editable error table)
 */

import { CircleAlert, CircleCheck, TriangleAlert } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

import ErrorDetailsTable from '../error-table/ErrorDetailsTable';
import FixErrorsActions from './components/FixErrorsActions';
import FixErrorsHeader from './components/FixErrorsHeader';
import FixErrorsModals from './components/FixErrorsModals';
import { useFixErrors, type FileDetail } from './hooks/useFixErrors';

interface FixErrorsPageProps {
  successData?: string;
  failedData?: string;
  dateInfo?: string;
  uploadedByInfo?: string;
  tableData?: string;
  setActiveStep?: (step: number) => void;
  setOrderCountInfo?: (count: number) => void;
  setFailedData?: (data: string) => void;
  setFileId?: (id: string) => void;
  setAmount?: (amount: number) => void;
  setSuccessData?: (data: string) => void;
  setFixErrorTableData?: (data: string) => void;
  fixErrorTableData?: string | null;
  fromTableDataFromNextPage?: unknown;
  setSkippedStepFromStepper?: (step: number) => void;
  fileState?: boolean;
  fileId?: string;
}

function FileDetailItem({ detail }: { detail: FileDetail }) {
  const StateIcon =
    detail.stateType === 'success'
      ? CircleCheck
      : detail.stateType === 'error'
        ? CircleAlert
        : null;
  const iconColor =
    detail.stateType === 'success'
      ? 'text-success'
      : detail.stateType === 'error'
        ? 'text-destructive'
        : '';

  return (
    <div className="flex flex-col gap-1">
      <Typography as="span" variant="small" className="text-muted-foreground">
        {detail.label}
      </Typography>
      <div className="flex items-center gap-1.5">
        {StateIcon && (
          <StateIcon className={cn('h-4 w-4 shrink-0', iconColor)} aria-hidden="true" />
        )}
        <Typography as="span" variant="body-medium">
          {detail.value}
        </Typography>
      </div>
    </div>
  );
}

interface LegendChipProps {
  variant: 'required' | 'optional';
  label: string;
  tooltip: string;
}

function LegendChip({ variant, label, tooltip }: LegendChipProps) {
  const isRequired = variant === 'required';
  const Icon = isRequired ? CircleAlert : TriangleAlert;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25',
              isRequired
                ? 'border-destructive/20 bg-destructive-soft text-destructive'
                : 'border-warning/20 bg-warning-soft text-warning',
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const FixErrorImports = (props: FixErrorsPageProps) => {
  const hook = useFixErrors({
    successData: props.successData ?? '',
    failedData: props.failedData ?? '',
    dateInfo: props.dateInfo ?? '',
    uploadedByInfo: props.uploadedByInfo ?? '',
    tableData: props.tableData ?? '[]',
    setActiveStep: props.setActiveStep ?? (() => {}),
    setOrderCountInfo: props.setOrderCountInfo ?? (() => {}),
    setFailedData: props.setFailedData ?? (() => {}),
    setFileId: props.setFileId ?? (() => {}),
    setAmount: props.setAmount ?? (() => {}),
    setSuccessData: props.setSuccessData ?? (() => {}),
    setFixErrorTableData: props.setFixErrorTableData ?? (() => {}),
    fixErrorTableData: props.fixErrorTableData ?? null,
    setSkippedStepFromStepper: props.setSkippedStepFromStepper ?? (() => {}),
    fileState: props.fileState ?? false,
    fileId: props.fileId ?? '',
  });

  const fileDetails = hook.getFileDetails();
  const hasFailed = Number(hook.failed) > 0;
  const sectionLabel = hook.isProperty ? 'Error Details' : 'Order Details';
  const requiredTooltip = hook.isProperty
    ? 'Errors marked in red are required and must be fixed to import the properties.'
    : 'Errors marked in red are required and must be fixed to place an order for that property.';
  const optionalTooltip = hook.isProperty
    ? 'Errors marked in yellow are optional and do not need to be fixed to import the properties.'
    : 'Errors marked in yellow are optional and do not need to be fixed to place an order for that property.';

  return (
    <div className="flex h-full flex-col bg-background">
      {hook.isProperty && <FixErrorsHeader onCancel={hook.onCancel} />}

      {fileDetails && (
        <section className="shrink-0 border-b border-border bg-card px-6 py-4">
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3">
            {fileDetails.map((detail, index) => (
              <FileDetailItem key={index} detail={detail} />
            ))}
          </div>
        </section>
      )}

      <section className="flex min-h-0 flex-1 flex-col px-6 pt-6">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Typography as="h2" variant="heading-md" className="font-semibold">
            {sectionLabel}
          </Typography>
          {hasFailed && (
            <div className="flex flex-wrap items-center gap-2">
              <LegendChip variant="required" label="Required" tooltip={requiredTooltip} />
              <LegendChip variant="optional" label="Optional" tooltip={optionalTooltip} />
            </div>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <ErrorDetailsTable
            isProperty={hook.isProperty}
            setResult={hook.setResult}
            isClearData={hook.isClearData}
            setIsClearData={hook.setIsClearData}
            setFileChangeAfterFix={hook.setFileChangeAfterFix}
            fileChangeAfterFix={hook.fileChangeAfterFix}
            setFixErrorTableData={props.setFixErrorTableData ?? (() => {})}
            fixErrorTableData={hook.isProperty ? null : props.fixErrorTableData ?? null}
            fixErrorTableDataForOrder={hook.fixErrorTableDataForOrder}
            ErrorTableData={hook.ErrorTableData}
            setDisabled={hook.setDisabled}
          />
        </div>
      </section>

      {fileDetails && (
        <FixErrorsActions
          isProperty={hook.isProperty}
          loading={hook.loading}
          onCancel={hook.onCancel}
          onBack={hook.onBack}
          onImport={hook.onImport}
        />
      )}

      <FixErrorsModals
        modal={hook.modal}
        dismissModal={hook.dismissModal}
        fixIssueLoading={hook.fixIssueLoading}
        popUpLoader={hook.popUpLoader}
        popUpLoaderForOrder={hook.popUpLoaderForOrder}
        fixIssues={hook.fixIssues}
        checkAndContinue={hook.checkAndContinue}
        importPropertiesWithNull={hook.importPropertiesWithNull}
        importOrdersWithNull={hook.importOrdersWithNull}
        importOrdersWithNullPartially={hook.importOrdersWithNullPartially}
      />
    </div>
  );
};
